/* ============================================================================
   Lime Eco Product Section — behavior
   Extracted from an inline <script> into a real theme asset so it's
   downloaded and parsed once per browsing session (cached), instead of
   being re-sent on every product page. Loaded with `defer`.

   Each section instance passes its dynamic (per-product, per-section)
   values through a small inline JSON config block (see
   [data-lime-config] in the section file) rather than this file being
   templated with Liquid itself — that's what makes this file safely
   static and cacheable.

   Optional config keys:
     - config.specsSectionId (string): if you've created a lightweight
       section that renders ONLY the specs markup, set this to that
       section's id and the compare modal will fetch via
       `?section_id=` instead of pulling the full product page HTML.
       If omitted, falls back to fetching the full page and extracting
       [data-lime-specs-body] from it (works out of the box, just heavier).
   ============================================================================ */
(function () {
  'use strict';

  // Global (document-level) listeners are attached once, not once per
  // section instance, to avoid duplicate work if [data-lime-section]
  // ever appears more than once on a page.
  var globalListenersAttached = false;

  function escapeAttr(str) {
    return String(str == null ? '' : str).replace(/"/g, '&quot;');
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = String(str == null ? '' : str);
    return div.innerHTML;
  }

  function attachGlobalListenersOnce() {
    if (globalListenersAttached) return;
    globalListenersAttached = true;

    // Close any open difficulty tooltip when clicking outside it.
    document.addEventListener('click', function (e) {
      document.querySelectorAll('[data-difficulty-tooltip].is-open').forEach(function (tooltip) {
        var root = tooltip.closest('[data-lime-product-root]');
        var toggle = root ? root.querySelector('[data-difficulty-toggle]') : null;
        if (!toggle) return;
        if (!tooltip.contains(e.target) && e.target !== toggle && !toggle.contains(e.target)) {
          tooltip.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    });

    // Close any open compare modal on Escape.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-lime-compare-modal]').forEach(function (modal) {
        if (modal.open && typeof modal.close === 'function') {
          modal.close();
        }
      });
    });
  }

  function initProductForm(wrapper, config) {
    var root = wrapper.querySelector('[data-lime-product-root]');
    if (!root) return;

    function formatMoney(cents) {
      var value = (cents / 100).toFixed(2);
      var moneyFormat = config.moneyFormat || '${{amount}}';
      var amountOccurrences = (moneyFormat.match(/\{\{\s*amount\s*\}\}/g) || []).length;
      if (amountOccurrences !== 1) {
        var symbolMatch = moneyFormat.match(/^[^\{]*/);
        var symbol = symbolMatch ? symbolMatch[0].trim() : '';
        return (symbol || '') + value;
      }
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value);
    }

    var variantsJsonEl = wrapper.querySelector('[data-lime-variant-json]');
    var variants = [];
    try { variants = JSON.parse(variantsJsonEl.textContent); } catch (e) { variants = []; }

    var colorOptionIndex = parseInt(root.getAttribute('data-color-option-index'), 10);
    var optionCount = variants.length ? (variants[0].options || []).length : 0;

    var state = [];
    root.querySelectorAll('[data-lime-option-group]').forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10);
      var selected = group.querySelector('.is-selected');
      state[pos] = selected ? selected.getAttribute('data-value') : null;
    });

    function findVariant(overrideState) {
      var s = overrideState || state;
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options || [];
        var ok = true;
        for (var p = 0; p < opts.length; p++) {
          if (s[p] != null && opts[p] !== s[p]) { ok = false; break; }
        }
        if (ok) return variants[i];
      }
      return null;
    }

    function setActiveMedia(mediaId) {
      var items = root.querySelectorAll('[data-media-id]');
      var matched = false;
      items.forEach(function (el) {
        if (String(el.getAttribute('data-media-id')) === String(mediaId)) {
          el.classList.add('is-active');
          matched = true;
        } else {
          el.classList.remove('is-active');
        }
      });
      root.querySelectorAll('.lime-product__thumb').forEach(function (t) {
        t.classList.toggle('is-active', String(t.getAttribute('data-media-id')) === String(mediaId));
      });
      return matched;
    }

    function swapImageByAltFallback(colorName) {
      if (!colorName) return false;
      var items = root.querySelectorAll('.lime-product__media-item');
      for (var i = 0; i < items.length; i++) {
        var alt = (items[i].getAttribute('data-media-alt') || '').toLowerCase();
        if (alt.indexOf(colorName.toLowerCase()) !== -1) {
          items.forEach(function (el) { el.classList.remove('is-active'); });
          items[i].classList.add('is-active');
          root.querySelectorAll('.lime-product__thumb').forEach(function (t) { t.classList.remove('is-active'); });
          return true;
        }
      }
      return false;
    }

    function updatePillPrices() {
      root.querySelectorAll('[data-lime-option-group]').forEach(function (group) {
        var pos = parseInt(group.getAttribute('data-option-position'), 10);
        if (pos === colorOptionIndex) return;
        group.querySelectorAll('[data-lime-option-value]').forEach(function (btn) {
          var priceEl = btn.querySelector('[data-lime-pill-price]');
          if (!priceEl) return;
          if (optionCount > 2) { priceEl.textContent = ''; return; }
          var probeState = state.slice();
          probeState[pos] = btn.getAttribute('data-value');
          var v = findVariant(probeState);
          priceEl.textContent = v ? formatMoney(v.price) : '';
          btn.classList.toggle('is-unavailable', !!v && !v.available);
        });
      });

      root.querySelectorAll('[data-option-type="color"] [data-lime-option-value]').forEach(function (btn) {
        var probeState = state.slice();
        probeState[colorOptionIndex] = btn.getAttribute('data-value');
        var v = findVariant(probeState);
        btn.classList.toggle('is-unavailable', !!v && !v.available);
      });
    }

    function updateUI(opts) {
      opts = opts || {};
      var variant = findVariant();
      var submitBtn = root.querySelector('[data-lime-submit]');
      var submitLabel = root.querySelector('[data-lime-submit-label]');

      if (!variant) {
        if (submitBtn) submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Unavailable';
        updatePillPrices();
        return;
      }

      root.querySelectorAll('[data-price]').forEach(function (el) { el.textContent = formatMoney(variant.price); });
      var stickyPrice = wrapper.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      var idInput = root.querySelector('[data-lime-variant-id]');
      if (idInput) idInput.value = variant.id;

      if (!opts.skipMediaSwap) {
        var colorName = colorOptionIndex > -1 ? state[colorOptionIndex] : null;
        if (variant.featured_media && variant.featured_media.id) {
          var didMatch = setActiveMedia(variant.featured_media.id);
          if (!didMatch) swapImageByAltFallback(colorName);
        } else {
          swapImageByAltFallback(colorName);
        }
      }

      var colorLabel = root.querySelector('[data-lime-option-name-display][data-option-position="' + colorOptionIndex + '"]');
      if (colorLabel && colorOptionIndex > -1 && state[colorOptionIndex]) colorLabel.textContent = state[colorOptionIndex];

      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        // Guard against clobbering the "Added ✓" state OR an in-flight
        // "Adding…" state if the shopper changes options mid-request.
        if (submitLabel && !submitBtn.classList.contains('is-added') && !submitBtn.disabled) {
          submitLabel.textContent = variant.available ? (config.submitLabel || 'Add to Cart') : 'Sold out';
        } else if (submitLabel && !submitBtn.classList.contains('is-added') && !variant.available) {
          // Still allow "Sold out" to show even though disabled=true was just set above.
          submitLabel.textContent = 'Sold out';
        }
      }

      updatePillPrices();

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    function aeroPulse(el) {
      if (!el) return;
      el.classList.remove('lime-aero-pulse');
      // Use double rAF instead of forcing a synchronous reflow via
      // offsetWidth — avoids blocking the main thread on every click.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.add('lime-aero-pulse');
        });
      });
      window.setTimeout(function () { el.classList.remove('lime-aero-pulse'); }, 400);
    }

    var PULSE_SELECTOR =
      '[data-lime-option-value], .lime-product__submit, .lime-product__utility-btn, .lime-product__help, .lime-product__qty-btn, .lime-product__kit-btn, .lime-product__addon-link, .lime-product__thumb';

    root.addEventListener('click', function (e) {
      // Pulse effect is folded into this single delegated handler instead
      // of registering a separate listener per matching element.
      var pulseTarget = e.target.closest(PULSE_SELECTOR);
      if (pulseTarget) aeroPulse(pulseTarget);

      var optBtn = e.target.closest('[data-lime-option-value]');
      if (optBtn) {
        var group = optBtn.closest('[data-lime-option-group]');
        var pos = parseInt(group.getAttribute('data-option-position'), 10);
        var value = optBtn.getAttribute('data-value');

        group.querySelectorAll('[data-lime-option-value]').forEach(function (b) { b.classList.remove('is-selected'); });
        optBtn.classList.add('is-selected');
        state[pos] = value;

        updateUI();
        return;
      }

      var thumb = e.target.closest('.lime-product__thumb');
      if (thumb) { setActiveMedia(thumb.getAttribute('data-media-id')); return; }

      if (e.target.closest('[data-qty-decrease], [data-qty-increase]')) {
        var isDec = !!e.target.closest('[data-qty-decrease]');
        var input = root.querySelector('[data-qty-input]');
        var current = parseInt(input.value, 10) || 1;
        input.value = isDec ? Math.max(1, current - 1) : current + 1;
        return;
      }

      var addon = e.target.closest('[data-lime-addon]');
      if (addon) {
        if (addon.disabled) return;

        var variantId = addon.getAttribute('data-variant-id');
        var handle = addon.getAttribute('data-handle');
        if (!variantId && !handle) return;

        var labelEl = addon.querySelector('[data-lime-addon-label]');
        var originalText = labelEl ? labelEl.textContent : addon.textContent;

        function setLabel(text) {
          if (labelEl) { labelEl.textContent = text; } else { addon.textContent = text; }
        }

        function addVariantToCart(id) {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: id, quantity: 1 })
          }).then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          });
        }

        addon.disabled = true;

        var addPromise = variantId
          ? addVariantToCart(variantId)
          : fetch('/products/' + handle + '.js')
              .then(function (r) { return r.json(); })
              .then(function (p) {
                var v = p.variants && p.variants[0];
                if (!v) throw new Error('No purchasable variant found');
                return addVariantToCart(v.id);
              });

        addPromise
          .then(function () {
            addon.classList.add('is-added');
            setLabel('Added ✓');
            document.dispatchEvent(new CustomEvent('cart:updated'));
            setTimeout(function () {
              addon.classList.remove('is-added');
              setLabel(originalText);
              addon.disabled = false;
            }, 2000);
          })
          .catch(function (err) {
            console.error('Lime product add-on error:', err);
            setLabel((err && err.description) ? err.description : 'Try again');
            addon.disabled = false;
            setTimeout(function () { setLabel(originalText); }, 2200);
          });
        return;
      }

      var difficultyToggle = e.target.closest('[data-difficulty-toggle]');
      if (difficultyToggle) {
        var tooltip = root.querySelector('[data-difficulty-tooltip]');
        if (tooltip) {
          var isOpen = tooltip.classList.toggle('is-open');
          difficultyToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }
        return;
      }

      var shareBtn = e.target.closest('[data-lime-share]');
      if (shareBtn) {
        var shareLabel = shareBtn.querySelector('[data-lime-share-label]');
        var shareData = { title: config.productTitle || document.title, url: window.location.href };
        if (navigator.share) {
          navigator.share(shareData).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(shareData.url).then(function () {
            if (shareLabel) {
              var prev = shareLabel.textContent;
              shareLabel.textContent = 'Link copied';
              setTimeout(function () { shareLabel.textContent = prev; }, 2000);
            }
          });
        }
        return;
      }
    });

    root.querySelectorAll('[data-difficulty-toggle]').forEach(function (toggle) {
      toggle.addEventListener('focus', function () {
        var tooltip = root.querySelector('[data-difficulty-tooltip]');
        if (tooltip) { tooltip.classList.add('is-open'); toggle.setAttribute('aria-expanded', 'true'); }
      });
      toggle.addEventListener('blur', function () {
        var tooltip = root.querySelector('[data-difficulty-tooltip]');
        if (tooltip) { tooltip.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); }
      });
    });

    var form = root.querySelector('[data-lime-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var submitBtn = root.querySelector('[data-lime-submit]');
        var submitLabel = root.querySelector('[data-lime-submit-label]');
        if (submitBtn.disabled) return;

        var formData = new FormData(form);
        submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = 'Adding…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          })
          .then(function () {
            submitBtn.classList.add('is-added');
            if (submitLabel) submitLabel.textContent = 'Added ✓';
            document.dispatchEvent(new CustomEvent('cart:updated'));
            setTimeout(function () {
              submitBtn.classList.remove('is-added');
              submitBtn.disabled = false;
              if (submitLabel) submitLabel.textContent = config.submitLabel || 'Add to Cart';
            }, 1800);
          })
          .catch(function (err) {
            console.error('Add to cart error:', err);
            submitBtn.disabled = false;
            if (submitLabel) submitLabel.textContent = (err && err.description) ? err.description : 'Try again';
            setTimeout(function () {
              if (submitLabel) submitLabel.textContent = config.submitLabel || 'Add to Cart';
            }, 2200);
          });
      });
    }

    updateUI({ skipMediaSwap: true });
  }

  /* --------------------------------------------------------------------
     Compare modal — lazy-loads specification content.
     The product picker grid still uses a small pre-rendered JSON list
     (handle/title/url/type/image/price — a few KB even for a couple
     hundred products), but the Specifications content for each product
     is fetched ON DEMAND, only for the 2-4 products a shopper actually
     selects, instead of every product's specs being rendered into the
     page on every single page load.
     -------------------------------------------------------------------- */
  function initCompareModal(wrapper, config) {
    var modal = wrapper.querySelector('[data-lime-compare-modal]');
    if (!modal) return;

    if (modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }

    var productsJsonEl = wrapper.querySelector('[data-lime-compare-products]');
    var products = [];
    try { products = JSON.parse(productsJsonEl.textContent); } catch (e) { products = []; }

    var currentProductHandle = config.productHandle;
    var maxCompare = config.maxCompare || 3;
    var selected = [];
    var specsCache = {};

    var grid = modal.querySelector('[data-lime-compare-grid]');
    var filtersWrap = modal.querySelector('[data-lime-compare-filters]');
    var countEl = modal.querySelector('[data-lime-compare-count]');
    var goBtn = modal.querySelector('[data-lime-compare-go]');
    var backBtn = modal.querySelector('[data-lime-compare-back]');
    var stepSelect = modal.querySelector('[data-compare-step="select"]');
    var stepResult = modal.querySelector('[data-compare-step="result"]');
    var columnsWrap = modal.querySelector('[data-lime-compare-columns]');

    function buildFilters() {
      var types = [];
      products.forEach(function (p) {
        if (p.type && types.indexOf(p.type) === -1) types.push(p.type);
      });
      var html = '<button type="button" class="lime-compare-filter-btn is-active" data-filter="all">All</button>';
      types.forEach(function (t) {
        html += '<button type="button" class="lime-compare-filter-btn" data-filter="' + escapeAttr(t) + '">' + escapeHtml(t) + '</button>';
      });
      filtersWrap.innerHTML = html;
    }

    function buildGrid() {
      var html = '';
      products.forEach(function (p) {
        var isSelected = selected.indexOf(p.handle) > -1;
        html += '' +
          '<button type="button" class="lime-compare-card' + (isSelected ? ' is-selected' : '') + '" data-handle="' + escapeAttr(p.handle) + '" data-type="' + escapeAttr(p.type) + '">' +
            '<span class="lime-compare-card__image-wrap">' +
              (p.image ? '<img src="' + p.image + '" alt="' + escapeAttr(p.title) + '" loading="lazy">' : '') +
              '<span class="lime-compare-card__check">' +
                '<svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</span>' +
            '</span>' +
            '<span class="lime-compare-card__name">' + escapeHtml(p.title) + '</span>' +
          '</button>';
      });
      grid.innerHTML = html;
    }

    function updateFooter() {
      countEl.textContent = selected.length + ' product' + (selected.length === 1 ? '' : 's') + ' selected';
      goBtn.disabled = selected.length < 2;
    }

    function openModal() {
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      if (typeof modal.close === 'function' && modal.open) {
        modal.close();
      } else {
        modal.removeAttribute('open');
        document.body.style.overflow = '';
      }
    }

    modal.addEventListener('close', function () {
      document.body.style.overflow = '';
    });

    filtersWrap.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-filter]');
      if (!btn) return;
      filtersWrap.querySelectorAll('[data-filter]').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      var filter = btn.getAttribute('data-filter');
      grid.querySelectorAll('.lime-compare-card').forEach(function (card) {
        var show = filter === 'all' || card.getAttribute('data-type') === filter;
        card.style.display = show ? '' : 'none';
      });
    });

    grid.addEventListener('mousedown', function (e) {
      var card = e.target.closest('.lime-compare-card');
      if (card) e.preventDefault();
    });

    grid.addEventListener('click', function (e) {
      var card = e.target.closest('.lime-compare-card');
      if (!card) return;
      var handle = card.getAttribute('data-handle');
      var idx = selected.indexOf(handle);
      if (idx > -1) {
        selected.splice(idx, 1);
        card.classList.remove('is-selected');
      } else {
        if (selected.length >= maxCompare) return;
        selected.push(handle);
        card.classList.add('is-selected');
      }
      updateFooter();
    });

    /* Fetch a product's Specifications content on demand.
       If config.specsSectionId is set, this uses Shopify's
       `?section_id=` rendering endpoint to fetch ONLY that section's
       markup — much smaller than downloading the full product page.
       Otherwise it falls back to fetching the full page and pulling
       [data-lime-specs-body] out of it (heavier, but works without any
       extra section setup).
       Only ever called for products the shopper actually selects to
       compare (max a handful at once), and cached per-handle so
       re-comparing the same product is free. */
    function fetchProductSpecs(handle) {
      if (specsCache[handle]) return Promise.resolve(specsCache[handle]);
      var product = products.filter(function (p) { return p.handle === handle; })[0];
      var baseUrl = product ? product.url : '/products/' + handle;

      var fetchUrl = config.specsSectionId
        ? baseUrl + (baseUrl.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(config.specsSectionId)
        : baseUrl;

      return fetch(fetchUrl)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var content;
          if (config.specsSectionId) {
            // The section-rendering endpoint returns just that section's
            // markup directly — no need to parse a whole document or
            // hunt for a specific node inside it.
            content = html && html.trim()
              ? html
              : '<div class="lime-compare-column__spec-body lime-compare-column__spec-empty">No specification details available.</div>';
          } else {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var el = doc.querySelector('[data-lime-specs-body]');
            content = el
              ? el.innerHTML
              : '<div class="lime-compare-column__spec-body lime-compare-column__spec-empty">No specification details available.</div>';
          }
          specsCache[handle] = content;
          return content;
        })
        .catch(function () {
          var fallback = '<div class="lime-compare-column__spec-body lime-compare-column__spec-empty">No specification details available.</div>';
          specsCache[handle] = fallback;
          return fallback;
        });
    }

    goBtn.addEventListener('click', function () {
      if (selected.length < 2) return;

      var handlesInOrder = selected.slice();

      var loadingHtml = handlesInOrder.map(function (handle) {
        var p = products.filter(function (pp) { return pp.handle === handle; })[0];
        if (!p) return '';
        return '' +
          '<div class="lime-compare-column" data-compare-column="' + escapeAttr(handle) + '">' +
            '<div class="lime-compare-column__image">' + (p.image ? '<img src="' + p.image + '" alt="' + escapeAttr(p.title) + '">' : '') + '</div>' +
            '<h3 class="lime-compare-column__title">' + escapeHtml(p.title) + '</h3>' +
            '<div class="lime-compare-column__spec-body lime-compare-column__spec-loading">Loading specifications…</div>' +
            '<a href="' + p.url + '" class="lime-compare-column__link">View Product</a>' +
          '</div>';
      }).join('');

      columnsWrap.innerHTML = loadingHtml;
      stepSelect.hidden = true;
      stepResult.hidden = false;

      handlesInOrder.forEach(function (handle) {
        fetchProductSpecs(handle).then(function (specHtml) {
          // Match on the actual attribute value rather than re-building
          // a selector string, so escaping differences between write
          // and read can never cause a lookup to silently fail.
          var col = Array.prototype.find.call(
            columnsWrap.querySelectorAll('[data-compare-column]'),
            function (c) { return c.getAttribute('data-compare-column') === handle; }
          );
          if (!col) return;
          var specEl = col.querySelector('.lime-compare-column__spec-loading');
          if (specEl) specEl.outerHTML = specHtml;
        });
      });
    });

    backBtn.addEventListener('click', function () {
      stepResult.hidden = true;
      stepSelect.hidden = false;
    });

    wrapper.querySelectorAll('[data-lime-compare-open]').forEach(function (btn) {
      btn.addEventListener('click', openModal);
    });
    modal.querySelectorAll('[data-lime-compare-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    if (currentProductHandle && products.some(function (p) { return p.handle === currentProductHandle; })) {
      selected.push(currentProductHandle);
    }

    buildFilters();
    buildGrid();
    updateFooter();
  }

  function init() {
    attachGlobalListenersOnce();

    document.querySelectorAll('[data-lime-section]').forEach(function (wrapper) {
      var configEl = wrapper.querySelector('[data-lime-config]');
      var config = {};
      try { config = JSON.parse(configEl.textContent); } catch (e) { config = {}; }

      initProductForm(wrapper, config);
      initCompareModal(wrapper, config);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();